import React from 'react';
import { Target, Eye, Rocket, Users, Network, Share2, Briefcase, GraduationCap, Globe, Heart, TrendingUp, Handshake, Sparkles } from 'lucide-react';

const ObjectivosDaComunidade = () => {
    const objectives = [
        {
            icon: <Users className="w-5 h-5" />,
            title: "Interacção Institucional",
            description: "Promover a interacção entre os graduados da UEM e a instituição, fortalecendo os laços acadêmicos."
        },
        {
            icon: <Heart className="w-5 h-5" />,
            title: "Sentimento de Pertença",
            description: "Fortalecer o sentimento de pertença e orgulho pela UEM entre todos os graduados."
        },
        {
            icon: <Network className="w-5 h-5" />,
            title: "Rede de Contactos",
            description: "Criar uma rede de contactos profissionais entre os graduados para troca de experiências."
        },
        {
            icon: <TrendingUp className="w-5 h-5" />,
            title: "Desenvolvimento Institucional",
            description: "Contribuir para o desenvolvimento e crescimento da UEM através de iniciativas diversas."
        },
        {
            icon: <Share2 className="w-5 h-5" />,
            title: "Partilha de Conhecimento",
            description: "Facilitar a partilha de experiências e conhecimentos entre diferentes gerações de graduados."
        },
        {
            icon: <Globe className="w-5 h-5" />,
            title: "Promoção da Imagem",
            description: "Promover a imagem da UEM na sociedade como instituição de excelência."
        },
        {
            icon: <GraduationCap className="w-5 h-5" />,
            title: "Apoio Académico",
            description: "Apoiar projectos académicos e de investigação da UEM para o avanço do conhecimento."
        },
        {
            icon: <Briefcase className="w-5 h-5" />,
            title: "Desenvolvimento Profissional",
            description: "Criar oportunidades de desenvolvimento profissional para os graduados."
        },
        {
            icon: <Handshake className="w-5 h-5" />,
            title: "Parcerias Estratégicas",
            description: "Estabelecer parcerias com instituições nacionais e internacionais em benefício da comunidade académica."
        }
    ];

    return (
        <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12">
            {/* Header Section */}
            <div className="mb-12 bg-emerald-50 rounded-xl p-6 border border-emerald-100/50">
                <div className="border-l-4 border-emerald-500 pl-4 mb-4">
                    <h1 className="text-3xl font- text-gray-800 tracking-tight">Objectivos da Comunidade</h1>
                    <p className="text-gray-500 text-base font-light mt-1">
                        Conheça os propósitos que guiam a Comunidade Alumni-UEM
                    </p>
                </div>
            </div>

            {/* Vision & Mission Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                {/* Vision Card */}
                <div className="bg-gradient-to-br from-blue-50/30 to-gray-50/30 rounded-xl p-6 border border-blue-100/50">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Eye className="w-5 h-5 text-blue-600" />
                        </div>
                        <h2 className="text-lg font-medium text-gray-700">Visão</h2>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                        Ser uma comunidade de referência que une gerações de graduados da UEM, promovendo a excelência académica, 
                        o desenvolvimento profissional e o impacto positivo na sociedade moçambicana e internacional.
                    </p>
                </div>

                {/* Mission Card */}
                <div className="bg-gradient-to-br from-emerald-50/30 to-gray-50/30 rounded-xl p-6 border border-emerald-100/50">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                            <Rocket className="w-5 h-5 text-emerald-600" />
                        </div>
                        <h2 className="text-lg font-medium text-gray-700">Missão</h2>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                        Fortalecer os laços entre os graduados e a UEM, criando um ambiente de colaboração, 
                        partilha de conhecimentos e oportunidades que contribuam para o crescimento pessoal, 
                        profissional e institucional, bem como para o desenvolvimento sustentável de Moçambique.
                    </p>
                </div>
            </div>

            {/* Objectives Section */}
            <div className="mb-12">
                <div className="flex items-center gap-2 mb-6">
                    <Target className="w-5 h-5 text-emerald-500" />
                    <h2 className="text-lg font-medium text-gray-700">Objectivos Principais</h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {objectives.map((objective, index) => (
                        <div 
                            key={index}
                            className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-all duration-300 group"
                        >
                            <div className="flex items-center justify-center mb-3">
                                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                                    <div className="text-emerald-500">
                                        {objective.icon}
                                    </div>
                                </div>
                            </div>
                            <h3 className="text-sm font-medium text-gray-700 text-center mb-2">
                                {objective.title}
                            </h3>
                            <p className="text-gray-500 text-xs text-center leading-relaxed">
                                {objective.description}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Impact Section */}
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl p-6 mb-10">
                <div className="flex justify-center mb-4">
                    <Sparkles className="w-6 h-6 text-white/80" />
                </div>
                <h3 className="text-lg font-light text-white text-center mb-6">
                    Impacto Esperado
                </h3>
                <div className="flex flex-row justify-around items-center">
                    <div className="text-center">
                        <div className="text-2xl font-light text-white mb-1">+10.000</div>
                        <div className="text-xs text-emerald-100">Graduados Impactados</div>
                    </div>
                    <div className="w-px h-8 bg-emerald-500/50"></div>
                    <div className="text-center">
                        <div className="text-2xl font-light text-white mb-1">50+</div>
                        <div className="text-xs text-emerald-100">Projectos de Colaboração</div>
                    </div>
                    <div className="w-px h-8 bg-emerald-500/50"></div>
                    <div className="text-center">
                        <div className="text-2xl font-light text-white mb-1">Anual</div>
                        <div className="text-xs text-emerald-100">Eventos e Encontros</div>
                    </div>
                </div>
            </div>

            {/* Values Section */}
            <div className="bg-gray-50/50 rounded-xl p-6 border border-gray-100 mb-10">
                <div className="flex items-center gap-2 mb-5">
                    <Heart className="w-4 h-4 text-emerald-500" />
                    <h3 className="text-sm font-medium text-gray-700">Valores Fundamentais</h3>
                </div>
                <div className="flex flex-wrap justify-center gap-8">
                    {['Excelência', 'Colaboração', 'Inovação', 'Compromisso'].map((value, index) => (
                        <div key={index} className="text-center">
                            <div className="text-sm font-medium text-emerald-600 mb-1">{value}</div>
                            <div className="text-xs text-gray-400">Valor</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Call to Action */}
            <div className="text-center">
                <button 
                    onClick={() => window.location.href = "/contactos"}
                    className="bg-emerald-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors inline-flex items-center gap-2 shadow-sm"
                >
                    <Handshake size={14} />
                    Participe da Comunidade
                </button>
            </div>
        </div>
    );
};

export default ObjectivosDaComunidade;